import React, { useState, useEffect, useMemo } from "react";
import { UploadCloud } from "lucide-react";
import { GoogleGenerativeAI } from "@google/generative-ai";

type ExtractedMetrics = {
  revenue: string;
  grossProfit: string;
  ebitda: string;
  ebit: string;
  netIncome: string;
  profitMargin: string;
  roic: string;
  roce: string;
  roe: string;
  roa: string;
};

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

const parseNum = (val: string) => {
  if (!val || val === "N/A") return 0;
  const cleaned = val.replace(/[^0-9.-]+/g, "");
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : 0;
};

const initialMetrics: ExtractedMetrics = {
  revenue: "N/A",
  grossProfit: "N/A",
  ebitda: "N/A",
  ebit: "N/A",
  netIncome: "N/A",
  profitMargin: "N/A",
  roic: "N/A",
  roce: "N/A",
  roe: "N/A",
  roa: "N/A",
};

const extractMetric = (text: string, labels: string[]): string => {
  const lower = text.toLowerCase();
  const lines = lower.split(/\r?\n/);

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const matchesLabel = labels.some((label) =>
      trimmed.startsWith(label.toLowerCase())
    );
    if (!matchesLabel) continue;

    const match = trimmed.match(/([-+]?\d[\d,.\s]*(?:[mbtk]|million|billion|thousand)?%?)/i);
    if (match) {
      return match[1].trim();
    }
  }

  return "N/A";
};

const extractMetricsFromText = (text: string): ExtractedMetrics => ({
  revenue: extractMetric(text, ["total revenue", "revenue"]),
  grossProfit: extractMetric(text, ["gross profit"]),
  ebitda: extractMetric(text, ["ebitda"]),
  ebit: extractMetric(text, ["ebit"]),
  netIncome: extractMetric(text, ["net income", "net profit"]),
  profitMargin: extractMetric(text, ["profit margin", "net margin"]),
  roic: extractMetric(text, ["roic", "return on invested capital"]),
  roce: extractMetric(text, ["roce", "return on capital employed"]),
  roe: extractMetric(text, ["roe", "return on equity"]),
  roa: extractMetric(text, ["roa", "return on assets"]),
});

const FinancialAnalytics: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [metrics, setMetrics] = useState<ExtractedMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceText, setSourceText] = useState<string | null>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "Upload financial documents, click Extract metrics, then ask me questions about the metrics or underlying performance.",
    },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const geminiModel = useMemo(() => {
    try {
      // CRA exposes only REACT_APP_*; Vite exposes VITE_* via import.meta.env
      const apiKey =
        (process.env.REACT_APP_GEMINI_API_KEY as string | undefined) ??
        (typeof import.meta !== "undefined" &&
          (import.meta as { env?: { VITE_GEMINI_API_KEY?: string } }).env
            ?.VITE_GEMINI_API_KEY);
      if (!apiKey) {
        return null;
      }
      const genAI = new GoogleGenerativeAI(apiKey);
      return genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to init Gemini model", e);
      return null;
    }
  }, []);

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files;
    if (!selected) return;
    setFiles(Array.from(selected));
    setMetrics(null);
    setError(null);
  };

  const handleProcessFiles = async () => {
    if (!files.length) return;

    setIsProcessing(true);
    setError(null);

    try {
      const texts = await Promise.all(
        files.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(String(reader.result || ""));
              reader.onerror = (e) => reject(e);
              reader.readAsText(file);
            })
        )
      );

      const combinedText = texts.join("\n\n");
      const extracted = extractMetricsFromText(combinedText);
      setMetrics(extracted);
      setSourceText(combinedText);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError("Failed to process files. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendChat = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    if (!geminiModel || !chatInput.trim()) return;

    const question = chatInput.trim();
    setChatInput("");
    setChatError(null);

    const userMessage: ChatMessage = { role: "user", content: question };
    setChatMessages((prev) => [...prev, userMessage]);
    setIsChatLoading(true);

    try {
    const contextParts: string[] = [];
    if (metrics) {
      contextParts.push(
        `Extracted metrics (JSON):\n${JSON.stringify(metrics, null, 2)}`
      );
    }
    if (sourceText) {
      contextParts.push(
        `Raw document text (truncated to 4000 chars):\n${sourceText.slice(
          0,
          4000
        )}`
      );
    }

    const systemPrompt =
      "You are an elite financial analyst AI. Use the provided metrics and document text to answer questions precisely and concisely. If something is uncertain, say so explicitly.";

    const fullPrompt =
      `${systemPrompt}\n\n` +
      contextParts.join("\n\n") +
      `\n\nUser question: ${question}`;

    const sleep = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const maxRetries = 2;
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await geminiModel.generateContent(fullPrompt);
        const text = result.response.text();

        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: text || "I could not generate a response.",
          },
        ]);
        return;
      } catch (e) {
        lastError = e;
        const is429 =
          (e as { status?: number })?.status === 429 ||
          /429|quota|rate limit|retry/i.test(String(e));
        if (is429 && attempt < maxRetries) {
          const waitSec = 10;
          setChatError(
            `Rate limit reached. Retrying in ${waitSec} seconds...`
          );
          await sleep(waitSec * 1000);
          setChatError(null);
        } else {
          break;
        }
      }
    }

    // eslint-disable-next-line no-console
    console.error(lastError);
    const errMsg = String(lastError);
    const isRateLimit =
      /429|quota|rate limit|Too Many Requests/i.test(errMsg);
    setChatError(
      isRateLimit
        ? "Gemini rate limit exceeded. Please wait a few minutes and try again, or check your quota at https://ai.google.dev/gemini-api/docs/rate-limits"
        : "Failed to get AI answer. Please try again."
    );
    } finally {
      setIsChatLoading(false);
    }
  };

  const revenueValue = useMemo(
    () => (metrics ? parseNum(metrics.revenue) : 0),
    [metrics]
  );

  useEffect(() => {
    // reset error when files change
    if (files.length === 0) return;
    setError(null);
  }, [files]);

  return (
    <div style={{ padding: "24px 0" }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
        Financial Analytics
      </h1>
      <p style={{ color: "#6b7280", marginBottom: 24 }}>
        Upload one or more financial reports (PDF exported as text, Word, or
        plain text). We will try to extract key high‑level metrics like
        revenue, profitability, and returns.
      </p>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          backgroundColor: "#f9fafb",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#eef2ff",
              color: "#4f46e5",
            }}
          >
            <UploadCloud size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>Upload financial documents</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Supported: text exports of annual reports, quarterly results,
              MD&amp;A, etc.
            </div>
          </div>
        </div>

        <input
          type="file"
          multiple
          onChange={handleFilesChange}
          style={{ marginBottom: 12 }}
        />

        {files.length > 0 && (
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
            Selected files:
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {files.map((file) => (
                <li key={file.name}>{file.name}</li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          disabled={!files.length || isProcessing}
          onClick={handleProcessFiles}
          style={{
            marginTop: 8,
            padding: "8px 16px",
            borderRadius: 999,
            border: "none",
            backgroundColor: !files.length || isProcessing ? "#9ca3af" : "#4f46e5",
            color: "#fff",
            cursor:
              !files.length || isProcessing ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {isProcessing ? "Analyzing..." : "Extract metrics"}
        </button>

        {error && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: "#b91c1c",
            }}
          >
            {error}
          </div>
        )}
      </div>

      {metrics && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
            Key metrics (experimental)
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            <MetricCard label="Total Revenue" value={metrics.revenue} />
            <MetricCard label="Gross Profit" value={metrics.grossProfit} />
            <MetricCard label="EBITDA" value={metrics.ebitda} />
            <MetricCard label="EBIT" value={metrics.ebit} />
            <MetricCard label="Net Income" value={metrics.netIncome} />
            <MetricCard label="Avg Profit Margin" value={metrics.profitMargin} />
            <MetricCard label="Avg ROIC" value={metrics.roic} />
            <MetricCard label="Avg ROCE" value={metrics.roce} />
            <MetricCard label="Avg ROE" value={metrics.roe} />
            <MetricCard label="Avg ROA" value={metrics.roa} />
          </div>

          <div
            style={{
              marginTop: 24,
              padding: 16,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              backgroundColor: "#fff",
            }}
          >
            <div style={{ fontSize: 13, color: "#4b5563" }}>
              Parsed revenue (numeric only, for internal checks):{" "}
              <strong>{revenueValue.toLocaleString()}</strong>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              This value is derived by stripping non‑numeric characters from the
              extracted revenue field. Please validate against the original
              report.
            </div>
          </div>

          <div style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              AI Assistant (Gemini)
            </h2>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
              Ask questions about the uploaded reports and extracted metrics.
              Answers are generated by Google Gemini using your{" "}
              <code>VITE_GEMINI_API_KEY</code>.
            </p>

            <div
              style={{
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                padding: 12,
                backgroundColor: "#f9fafb",
              }}
            >
              <div
                style={{
                  maxHeight: 220,
                  overflowY: "auto",
                  padding: 8,
                  backgroundColor: "#ffffff",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                }}
              >
                {chatMessages.map((m, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: 8,
                      textAlign: m.role === "user" ? "right" : "left",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-block",
                        padding: "6px 10px",
                        borderRadius: 999,
                        backgroundColor:
                          m.role === "user" ? "#4f46e5" : "#e5e7eb",
                        color: m.role === "user" ? "#ffffff" : "#111827",
                        fontSize: 13,
                      }}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    Gemini is thinking...
                  </div>
                )}
              </div>

              {chatError && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: "#b91c1c",
                  }}
                >
                  {chatError}
                </div>
              )}

              <form
                onSubmit={handleSendChat}
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 10,
                }}
              >
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask a question about these metrics..."
                  style={{
                    flex: 1,
                    padding: "6px 10px",
                    borderRadius: 999,
                    border: "1px solid #d1d5db",
                    fontSize: 13,
                  }}
                />
                <button
                  type="submit"
                  disabled={isChatLoading}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    border: "none",
                    backgroundColor: isChatLoading ? "#9ca3af" : "#4f46e5",
                    color: "#ffffff",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: isChatLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isChatLoading ? "Sending..." : "Ask"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {!metrics && !isProcessing && (
        <div
          style={{
            marginTop: 12,
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          No metrics extracted yet. Upload one or more documents and click{" "}
          <strong>Extract metrics</strong> to get started.
        </div>
      )}
    </div>
  );
};

type MetricCardProps = {
  label: string;
  value: string;
};

const MetricCard: React.FC<MetricCardProps> = ({ label, value }) => (
  <div
    style={{
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      padding: 12,
      backgroundColor: "#ffffff",
      minHeight: 70,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <div style={{ fontSize: 11, textTransform: "uppercase", color: "#6b7280" }}>
      {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
      {value || "N/A"}
    </div>
  </div>
);

export default FinancialAnalytics;

